import { costPerSortChartType } from "../costPerSort";
import { useGroupDesertsChartType } from "../useGroupDeserts";
import { areaSheetChartType } from "../areaSheet";
import { spaceAllocationChartType } from "../spaceAllocation";
import type { ChartCardProps, ChartOption, EdiphiChartType } from "./index";
import { optionsAsEventData, splitChartTitle } from "../helpers/common";
import Logger from "../../../clients/sentry";
import { serializeErrorForWarning } from "../../../errors";
import { clearReportTables } from "../../datasources/reportTables";
import { track } from "../../../clients/mixpanel";
import type { UserSettings } from "../../../interfaces/UserSettings";

/**
 * Every chart the add-in can build. Kept apart from `common.ts` because each chart module imports
 * the shared primitives from there — a registry in the same file makes that circular.
 */
export const ediphiChartTypes: EdiphiChartType[] = [
  costPerSortChartType,
  spaceAllocationChartType,
  useGroupDesertsChartType,
  areaSheetChartType,
];

export const discoverExistingCharts = async (): Promise<[ChartCardProps[], string[]]> => {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const worksheets = workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const existingCharts: ChartCardProps[] = [];
    const linkedTableNames: string[] = [];

    // Every sheet's charts in one round trip, rather than one per sheet.
    const sheetCharts = worksheets.items.map((worksheet) => ({
      worksheet,
      charts: worksheet.charts.load("items/name,chartType,title"),
    }));
    await context.sync();

    for (const { worksheet, charts } of sheetCharts) {
      for (const chart of charts.items) {
        const ediphiChartType = ediphiChartTypes.find((ct) => chart.name.includes(ct.key));

        if (ediphiChartType) {
          const rawOptions = await ediphiChartType.getOptions(worksheet.name, chart.name);

          const currentOptions: Record<string, ChartOption> = {};
          for (const option of Object.values(rawOptions)) {
            if (option.getSelectedOptionItem) {
              try {
                const currentItem = await option.getSelectedOptionItem(worksheet.name, chart.name);
                // A resolver can come back empty; fall back to the default, as the catch below does.
                currentOptions[option.key] = {
                  ...option,
                  selectedOptionItem: currentItem ?? option.selectedOptionItem,
                };
              } catch (error) {
                // ???: Change to logger.warn?
                console.warn(`Failed to get current value for option ${option.key}:`, error);
                currentOptions[option.key] = option;
              }
            } else {
              currentOptions[option.key] = option;
            }
          }
          const { title: chartTitle, subTitle: chartSubtitle } = splitChartTitle(chart.title?.text);
          linkedTableNames.push(chart.name);
          existingCharts.push({
            worksheetName: worksheet.name,
            chartName: chart.name,
            ediphiChartType,
            chartOptions: currentOptions,
            title: chartTitle,
            subTitle: chartSubtitle,
          });
        }
      }
    }

    return [existingCharts, linkedTableNames];
  });
};

/**
 * Empties every chart's data table, leaving the chart and its hidden sheet in place so a later estimate
 * load refills it. Refreshing cannot do this: upsertChartTable returns early on no rows, which leaves the
 * previous data — and so the chart — standing.
 */
export const clearAllChartData = async (): Promise<void> => {
  // A chart's table carries the chart's own name, which is what discovery collects.
  const [, linkedTableNames] = await discoverExistingCharts();
  if (linkedTableNames.length) await clearReportTables(...linkedTableNames);
};

export const refreshAllCharts = async (userSettings: UserSettings): Promise<number> => {
  const start = performance.now();
  const [charts] = await discoverExistingCharts();
  const chartCount = charts.length;
  if (chartCount === 0) {
    return 0;
  }

  const errors: Error[] = [];

  for (const chart of charts) {
    try {
      const refreshStartTime = performance.now();
      await chart.ediphiChartType.chartRefresher(chart.worksheetName, chart.chartName, chart.chartOptions);
      const refreshEndTime = performance.now();
      const refreshDuration = (refreshEndTime - refreshStartTime) / 1000;

      track("excel.chart.refresh", {
        ediphiChartType: chart.ediphiChartType.key,
        useEdiphiColors: userSettings.useEdiphiColors,
        loadTime: refreshDuration,
        ...optionsAsEventData(chart.chartOptions),
      });
    } catch (error) {
      // Error normalization for non-Error types
      const err = error instanceof Error ? error : new Error("Unknown error during chart refresh");
      errors.push(err);
    }
  }

  if (errors.length > 0) {
    // ???: Throw if all charts failed?
    // if (chartCount === errors.length) {
    //   throw new ValidationError("All charts failed to refresh.");
    // }
    // Breadcrumb for partial failures; generic Error retained for aggregated context
    const aggregatedError = new Error(errors.map((e) => e.message).join("; "));
    Logger.warn(
      "Errors occurred while refreshing charts",
      serializeErrorForWarning(aggregatedError, {
        errorCount: errors.length,
      })
    );
  }

  const end = performance.now();
  const totalDuration = (end - start) / 1000;
  track("excel.chart.refresh.all", {
    loadTime: totalDuration,
    chartCount: charts.length,
  });

  return chartCount;
};
